/**
 * The conditions that make each of the twelve missions itself (03 §2-§3): noise discipline,
 * what the garrison does when alerted, being seen where it matters, and mission 11's signal.
 */

import { type Ctx, isFighter } from '../ctx';
import { explode, removeProp } from '../damage';
import { OBJECTIVES, secs } from '../tuning';
import type { MissionDef, ObjectiveRule, Rect } from '../types';
import { expansionStillApplies, fireExpansionEffect } from './alertEffects';
import { dist, failObjective, findRule, inRect, loseCaptive, propDef, targetProps } from './common';
import { wearsUniform } from './disguise';
import { reinforceTarget } from './dormant';
import type { PlayerNoise } from './noise';
import { officerAtLarge } from './officer';

type AlertRule = Extract<ObjectiveRule, { type: 'alertConsequence' }>;

/**
 * Where noise discipline ends: the quiet rule's zone, or - on a mission that offers the
 * no-contact bonus without one - the goal zone, with the manual's penalty (02 §11).
 */
function quietRule(def: MissionDef): { zone: Rect; breach: 'fail' | 'penalty' } | null {
  const rule = findRule(def, 'quietUntilZone');
  if (rule) return rule;
  return findRule(def, 'noContactBonus') ? { zone: def.goalZone, breach: 'penalty' } : null;
}

function loseHeldCaptives(ctx: Ctx): void {
  for (const c of ctx.world.objective.captives) if (c.state === 'held') loseCaptive(ctx, c);
}

function stepQuiet(ctx: Ctx, noise: PlayerNoise): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  const quiet = quietRule(statics.def);
  if (o.enteredZone) return;
  // Entering is checked first: the shot that opens the fight inside the wire is allowed.
  // (`enteredZone` is kept on every mission - against the goal zone when there is no quiet rule.)
  if (inRect(quiet?.zone ?? statics.def.goalZone, w.ranger.x, w.ranger.z)) {
    o.enteredZone = true;
    return;
  }
  if (!quiet || !noise.loud || o.contactBroken) return;
  if (quiet.breach === 'penalty') {
    // The objective can still be had; the debrief takes it out of his score (02 §11).
    o.contactBroken = true;
    return;
  }
  if (statics.def.captives.length > 0) {
    loseHeldCaptives(ctx);
    failObjective(ctx, 'THE PRISONERS WERE MOVED');
  } else failObjective(ctx, 'CONTACT BEFORE THE GOAL ZONE');
}

/** Can this effect still happen? If not, the rule is spent without firing. */
function stillApplies(ctx: Ctx, rule: AlertRule): boolean {
  const { world: w, statics } = ctx;
  const o = w.objective;
  switch (rule.effect) {
    case 'jetsScramble':
      return !o.complete;
    case 'prisonersMoved':
      return !o.opened;
    case 'demolition': {
      if (o.opened) return false;
      // Cut it by killing the man with the detonator (03 §2, mission 10) - if the garrison has one.
      const hasOfficer = statics.def.spawns.some((s) => s.kind === 'officer');
      return !hasOfficer || w.enemies.some((e) => e.kind === 'officer' && isFighter(e));
    }
    case 'officerFlees':
      return officerAtLarge(ctx) !== null;
    case 'reinforceTarget':
      return true;
    default:
      // The expansion's effects (10 §3) live in alertEffects.ts.
      return expansionStillApplies(ctx, rule.effect);
  }
}

function fire(ctx: Ctx, rule: AlertRule): void {
  const { world: w, statics } = ctx;
  const target = targetProps(statics.def)[0];
  switch (rule.effect) {
    case 'jetsScramble':
      // The runway empties (03 §2, mission 3): whatever he has not already wrecked takes off.
      for (const jet of targetProps(statics.def)) removeProp(ctx, jet.id);
      failObjective(ctx, 'THE JETS SCRAMBLED');
      break;
    case 'prisonersMoved':
      loseHeldCaptives(ctx);
      failObjective(ctx, 'THE PRISONERS WERE MOVED');
      break;
    case 'demolition': {
      loseHeldCaptives(ctx);
      const prison = statics.def.props.find((p) => p.kind === 'prison') ?? target;
      if (prison) explode(ctx, prison.x, prison.z, OBJECTIVES.demolitionRadius, 'heavy', false);
      failObjective(ctx, 'THE PRISON WAS DEMOLISHED');
      break;
    }
    case 'officerFlees':
      w.objective.officerFleeing = true;
      break;
    case 'reinforceTarget':
      if (target) reinforceTarget(ctx, target);
      break;
    default:
      fireExpansionEffect(ctx, rule, rule.effect);
  }
}

function stepAlertConsequences(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const o = w.objective;
  let soonest = -1;
  for (const st of o.consequences) {
    const rule = statics.def.objective.rules[st.rule];
    if (st.spent || !rule || rule.type !== 'alertConsequence') continue;
    if (!stillApplies(ctx, rule)) {
      st.spent = true;
      st.ticks = -1;
      continue;
    }
    if (st.ticks < 0) {
      // "Before the ranger is inside": once he is in, this rule is off for good.
      if (rule.unlessInsideZone && inRect(rule.unlessInsideZone, w.ranger.x, w.ranger.z)) {
        st.spent = true;
        continue;
      }
      if (w.alert < rule.level) continue;
      st.ticks = secs(rule.delaySeconds);
    }
    if (st.ticks > 0) st.ticks--;
    if (st.ticks === 0) {
      st.spent = true;
      st.ticks = -1;
      fire(ctx, rule);
    } else if (soonest < 0 || st.ticks < soonest) soonest = st.ticks;
  }
  o.consequenceTicks = soonest;
}

/** Missions 9 and 12: a soldier who spots the ranger close to the target has seen too much. */
function stepNotSeenNear(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  for (const rule of statics.def.objective.rules) {
    if (rule.type !== 'notSeenNear') continue;
    const prop = propDef(statics.def, rule.propId);
    if (!prop || dist(prop.x, prop.z, r.x, r.z) > rule.radius) continue;
    for (const ev of ctx.events) {
      if (ev.type !== 'spotted') continue;
      // A motion detector reports to nobody.
      const spotter = w.enemies.find((e) => e.id === ev.id);
      if (spotter && !wearsUniform(spotter.kind)) continue;
      const kind = statics.def.objective.kind;
      const planted = kind === 'plantAtTarget' || kind === 'plantAtTargets';
      failObjective(ctx, rule.reason ?? (planted ? 'THE BOMB WAS FOUND' : 'SEEN AT THE TARGET'));
      return;
    }
  }
}

/** Mission 11: lie low until the signal, then be heard - and soon. */
function stepSignal(ctx: Ctx, noise: PlayerNoise): void {
  const o = ctx.world.objective;
  const rule = findRule(ctx.statics.def, 'signal');
  if (!rule) return;
  if (!o.signalled) {
    if (o.groundTicks >= secs(rule.atSeconds)) {
      o.signalled = true;
      o.quietTicks = 0;
      ctx.events.push({ type: 'signal' });
    } else if (noise.loud) o.contactBroken = true;
    return;
  }
  if (o.wentLoud) return;
  if (noise.loud) {
    o.wentLoud = true;
    return;
  }
  o.quietTicks++;
  const limit = secs(rule.failIfQuietForSeconds);
  // The warning tone, while there is still time to act on it.
  if (limit > OBJECTIVES.signalWarningTicks && o.quietTicks === limit - OBJECTIVES.signalWarningTicks) {
    ctx.events.push({ type: 'signal' });
  }
  if (o.quietTicks >= limit) failObjective(ctx, 'NO DIVERSION: STAYED QUIET TOO LONG');
}

export function stepRules(ctx: Ctx, noise: PlayerNoise): void {
  const o = ctx.world.objective;
  stepQuiet(ctx, noise);
  if (!o.failed) stepSignal(ctx, noise);
  if (!o.failed) stepAlertConsequences(ctx);
  if (!o.failed) stepNotSeenNear(ctx);
}
